/**
 * ImportHubModal · Iter254
 *
 * Multi-format import surface used from BOTH the Outreach panel toolbar
 * and the *Editar & Reenviar* drawer. Two tabs:
 *
 *   1. **Templates**  — drag / pick `.txt` `.md` `.docx` OR paste raw text
 *                       and save as a reusable outreach template.
 *   2. **Mídia**      — drag / pick `.png` `.jpg` `.webp` images, uploaded
 *                       to Emergent Object Storage. Returns a URL that
 *                       operators paste into email bodies.
 *
 * Backend:
 *   POST /api/smith/outreach/imports/template   (multipart)
 *   POST /api/smith/outreach/templates          (JSON — reused for paste)
 *   POST /api/smith/outreach/imports/asset      (multipart)
 *   GET  /api/smith/outreach/imports/assets     (list)
 *
 * Emits `onTemplateImported(tpl)` and `onAssetImported(asset)` so the
 * parent screen can refresh its own state without a full re-fetch.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, FileText, Image as ImageIcon, X, Loader2,
         ClipboardPaste, CheckCircle2, Copy, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEMPLATE_EXTS = [".txt", ".md", ".markdown", ".docx"];
const ASSET_EXTS = [".png", ".jpg", ".jpeg", ".webp"];

export default function ImportHubModal({ open, onClose,
                                         onTemplateImported,
                                         onAssetImported }) {
  const [tab, setTab] = useState("templates");
  const [uploading, setUploading] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [pasteName, setPasteName] = useState("");
  const [assets, setAssets] = useState([]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [dragging, setDragging] = useState(false);
  const tplInputRef = useRef(null);
  const imgInputRef = useRef(null);

  useEffect(() => { if (open && tab === "assets") loadAssets(); },
    [open, tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAssets = async () => {
    setLoadingAssets(true);
    try {
      const { data } = await axios.get(`${API}/smith/outreach/imports/assets`);
      setAssets(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao carregar assets");
    } finally { setLoadingAssets(false); }
  };

  const doUploadTemplate = async (file) => {
    if (!file) return;
    const lower = file.name.toLowerCase();
    if (!TEMPLATE_EXTS.some((e) => lower.endsWith(e))) {
      toast.error(`Formato não suportado — use ${TEMPLATE_EXTS.join(", ")}`);
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(
        `${API}/smith/outreach/imports/template`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Template importado: ${data.name}`);
      onTemplateImported && onTemplateImported(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao importar template");
    } finally { setUploading(false); }
  };

  const doPasteTemplate = async () => {
    const body = pasteText.trim();
    if (body.length < 10) {
      toast.error("Cole ao menos 10 caracteres");
      return;
    }
    // Try to split subject on first line if it looks like "Subject: ..."
    let subject = pasteName.trim();
    let bodyToSave = body;
    const m = body.match(/^\s*(?:subject|assunto)\s*:\s*(.+)$/im);
    if (m && !subject) {
      subject = m[1].trim().slice(0, 200);
      bodyToSave = body.replace(m[0], "").trim();
    }
    if (!subject) {
      subject = `Colado ${new Date().toLocaleDateString("pt-BR")}`;
    }
    setUploading(true);
    try {
      const { data } = await axios.post(`${API}/smith/outreach/templates`, {
        name: (pasteName.trim() || subject).slice(0, 80),
        subject,
        body: bodyToSave.slice(0, 8000),
        tone: "consultative",
        language: "pt",
      });
      toast.success(`Template salvo: ${data.name}`);
      setPasteText(""); setPasteName("");
      onTemplateImported && onTemplateImported(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar template");
    } finally { setUploading(false); }
  };

  const doUploadAsset = async (file) => {
    if (!file) return;
    const lower = file.name.toLowerCase();
    if (!ASSET_EXTS.some((e) => lower.endsWith(e))) {
      toast.error(`Formato não suportado — use ${ASSET_EXTS.join(", ")}`);
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Imagem muito grande (>8MB)");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(
        `${API}/smith/outreach/imports/asset`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imagem enviada: ${data.filename}`);
      onAssetImported && onAssetImported(data);
      await loadAssets();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao subir imagem");
    } finally { setUploading(false); }
  };

  const deleteAsset = async (id) => {
    if (!window.confirm("Remover este asset?")) return;
    try {
      await axios.delete(`${API}/smith/outreach/imports/asset/${id}`);
      setAssets((cur) => cur.filter((a) => a.id !== id));
      toast.success("Asset removido");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao remover");
    }
  };

  const copyUrl = (url) => {
    const abs = url.startsWith("http") ? url : `${API.replace("/api", "")}${url}`;
    navigator.clipboard.writeText(abs);
    toast.success("URL copiada");
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (!f) return;
    if (tab === "templates") doUploadTemplate(f);
    else doUploadAsset(f);
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4"
         style={{ background: "rgba(2,6,23,0.75)" }}
         onClick={onClose}
         data-testid="import-hub-modal">
      <div className="w-full max-w-[720px] max-h-[90vh] rounded-2xl border overflow-hidden flex flex-col"
           style={{ background: "#0B1120", borderColor: "rgba(220,38,38,0.30)" }}
           onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
          <div>
            <div className="text-white text-[15px] font-black">Import Hub</div>
            <div className="text-[11px] text-slate-400">
              Templates prontos e mídia — normalizado para o motor Resend
            </div>
          </div>
          <button type="button" onClick={onClose}
                  data-testid="import-hub-close"
                  className="w-8 h-8 rounded hover:bg-white/10 text-slate-400 flex items-center justify-center">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/10 shrink-0" data-testid="import-hub-tabs">
          {[
            { id: "templates", label: "Templates", icon: FileText },
            { id: "assets",    label: "Mídia",     icon: ImageIcon },
          ].map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button key={t.id} type="button"
                      data-testid={`import-hub-tab-${t.id}`}
                      onClick={() => setTab(t.id)}
                      className={`px-4 py-2.5 text-[12.5px] font-black inline-flex items-center gap-1.5 border-b-2 -mb-px ${active ? "border-[#DC2626] text-white" : "border-transparent text-slate-400 hover:text-white"}`}>
                <Icon className="w-3.5 h-3.5" />{t.label}
              </button>
            );
          })}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {tab === "templates" && (
            <>
              <DropZone
                testid="import-hub-templates-drop"
                dragging={dragging}
                onDragEnter={() => setDragging(true)}
                onDragLeave={() => setDragging(false)}
                onDrop={onDrop}
                onPick={() => tplInputRef.current?.click()}
                uploading={uploading}
                accept={TEMPLATE_EXTS.join(",")}
                label="Arraste um arquivo .txt, .md ou .docx"
                sub="ou clique para escolher · máx 2MB" />
              <input ref={tplInputRef} type="file" hidden accept={TEMPLATE_EXTS.join(",")}
                     data-testid="import-hub-templates-input"
                     onChange={(e) => doUploadTemplate(e.target.files?.[0])} />

              {/* Paste form */}
              <div className="rounded-xl border p-4 space-y-2"
                   style={{ background: "rgba(15,23,42,0.6)", borderColor: "rgba(255,255,255,0.08)" }}>
                <div className="text-[11px] font-black uppercase tracking-widest text-slate-400 inline-flex items-center gap-1.5">
                  <ClipboardPaste className="w-3 h-3" /> ou cole um bloco de texto
                </div>
                <input value={pasteName}
                       onChange={(e) => setPasteName(e.target.value)}
                       data-testid="import-hub-paste-name"
                       placeholder="Nome do template (opcional)"
                       className="w-full h-9 px-3 rounded-lg bg-white/5 border border-white/10 text-[12.5px] text-white" />
                <textarea value={pasteText}
                          onChange={(e) => setPasteText(e.target.value)}
                          data-testid="import-hub-paste-text"
                          rows={6} maxLength={8000}
                          placeholder="Cole aqui — a primeira linha 'Subject:' vira o assunto do template."
                          className="w-full rounded-lg bg-white/5 border border-white/10 text-[12.5px] text-slate-100 p-3 resize-vertical" />
                <div className="flex items-center justify-between">
                  <div className="text-[10.5px] text-slate-500 tabular-nums">
                    {pasteText.length}/8000
                  </div>
                  <button type="button"
                          onClick={doPasteTemplate}
                          disabled={uploading || pasteText.trim().length < 10}
                          data-testid="import-hub-paste-save"
                          className="h-9 px-3 rounded-lg text-[12px] font-black text-white inline-flex items-center gap-1.5 disabled:opacity-40"
                          style={{ background: "linear-gradient(135deg,#DC2626 0%,#7F1D1D 100%)" }}>
                    {uploading
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <CheckCircle2 className="w-3.5 h-3.5" />}
                    Salvar template
                  </button>
                </div>
              </div>
            </>
          )}

          {tab === "assets" && (
            <>
              <DropZone
                testid="import-hub-assets-drop"
                dragging={dragging}
                onDragEnter={() => setDragging(true)}
                onDragLeave={() => setDragging(false)}
                onDrop={onDrop}
                onPick={() => imgInputRef.current?.click()}
                uploading={uploading}
                accept={ASSET_EXTS.join(",")}
                label="Arraste uma imagem .png / .jpg / .webp"
                sub="ou clique para escolher · máx 8MB" />
              <input ref={imgInputRef} type="file" hidden accept={ASSET_EXTS.join(",")}
                     data-testid="import-hub-assets-input"
                     onChange={(e) => doUploadAsset(e.target.files?.[0])} />

              {/* Uploaded assets grid */}
              <div className="pt-1">
                <div className="text-[11px] font-black uppercase tracking-widest text-slate-400 mb-2">
                  Mídia já importada
                </div>
                {loadingAssets ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-5 h-5 animate-spin text-slate-500" />
                  </div>
                ) : assets.length === 0 ? (
                  <div className="text-[12px] text-slate-500 text-center py-6"
                       data-testid="import-hub-assets-empty">
                    Nenhuma imagem ainda — arraste uma acima.
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3"
                       data-testid="import-hub-assets-grid">
                    {assets.map((a) => (
                      <AssetCard key={a.id} asset={a}
                                 onCopy={() => copyUrl(a.download_url)}
                                 onDelete={() => deleteAsset(a.id)} />
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


function DropZone({ dragging, onDragEnter, onDragLeave, onDrop, onPick,
                    uploading, label, sub, testid }) {
  return (
    <div
      data-testid={testid}
      onDragEnter={(e) => { e.preventDefault(); onDragEnter(); }}
      onDragOver={(e) => e.preventDefault()}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onClick={onPick}
      role="button" tabIndex={0}
      className={`rounded-xl border-2 border-dashed p-8 flex flex-col items-center justify-center gap-2 cursor-pointer transition ${dragging ? "border-[#DC2626] bg-red-500/10" : "border-white/15 hover:border-white/30"}`}
      style={{ background: "rgba(15,23,42,0.55)" }}>
      {uploading
        ? <Loader2 className="w-8 h-8 animate-spin text-[#DC2626]" />
        : <Upload className="w-8 h-8 text-slate-400" />}
      <div className="text-[13px] font-black text-white">{label}</div>
      <div className="text-[11px] text-slate-400">{sub}</div>
    </div>
  );
}


function AssetCard({ asset, onCopy, onDelete }) {
  const abs = asset.download_url.startsWith("http")
    ? asset.download_url
    : `${process.env.REACT_APP_BACKEND_URL}${asset.download_url}`;
  return (
    <div className="rounded-lg border overflow-hidden bg-black/40"
         style={{ borderColor: "rgba(255,255,255,0.08)" }}
         data-testid={`import-hub-asset-${asset.id}`}>
      <div className="aspect-square bg-slate-900 flex items-center justify-center overflow-hidden">
        <img src={abs} alt={asset.filename}
             className="w-full h-full object-cover"
             onError={(e) => { e.target.style.display = "none"; }} />
      </div>
      <div className="px-2 py-1.5 space-y-1">
        <div className="text-[10.5px] text-white truncate font-bold" title={asset.filename}>
          {asset.filename}
        </div>
        <div className="flex items-center gap-1">
          <button type="button" onClick={onCopy}
                  data-testid={`import-hub-asset-copy-${asset.id}`}
                  title="Copiar URL"
                  className="flex-1 h-6 rounded text-[10px] font-bold text-emerald-300 bg-emerald-500/15 hover:bg-emerald-500/30 inline-flex items-center justify-center gap-1">
            <Copy className="w-3 h-3" />URL
          </button>
          <button type="button" onClick={onDelete}
                  data-testid={`import-hub-asset-delete-${asset.id}`}
                  title="Remover"
                  className="w-6 h-6 rounded text-red-300 bg-red-500/15 hover:bg-red-500/30 inline-flex items-center justify-center">
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
}
